import { Spacing } from '@persona-id/design-tokens';
import IconMore from '@persona-id/design-tokens/icons/MoreIcon';
import { useState } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import DynamicInput from 'dashboard/components/DynamicInput';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import WorkflowStatusSticker from 'dashboard/components/WorkflowStatusSticker';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { FullScreenHeaderContainer } from 'dashboard/layouts/ShowViewLayoutV2/ShowViewLayout';
import { AccountAction } from 'dashboard/models/account-action';
import { Workflow, WorkflowConfigError, WorkflowStatus } from 'dashboard/models/workflow';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import AccountActionVersionStatusSticker from 'dashboard/views/DashboardAccountActionsShowView/AccountActionVersionStatusSticker';
import WorkflowValidationErrorsDropdown from 'dashboard/views/DashboardWorkflowsShowView/WorkflowValidationErrorsDropdown';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { HStack } from 'design-system/components/Stack';
import useConfirmation from 'lib/hooks/useConfirmation';

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StatusWrapper = styled.div`
  display: flex;
  padding-left: ${Spacing[2]};
  height: 22px;
`;

interface AccountActionHeaderProps {
  isLoading: boolean;
  isDirty: boolean;
  isLatestPublishedVersion: boolean;
  isShowingWorkflowVersions: boolean;
  editable: boolean;
  accountTypeId: string;
  accountAction: AccountAction;
  actionWorkflow: Workflow;
  actionWorkflowVersion: WorkflowVersion;
  validationErrors: WorkflowConfigError[];
  showWorkflowVersionsView: () => void;
  closeWorkflowVersionsView: () => void;
  handleActivateAccountAction: () => Promise<void>;
  handleDeactivateAccountAction: () => Promise<void>;
  handleSaveAccountAction: () => Promise<void>;
  handlePublishAccountAction: () => Promise<void>;
  handleUpdateAccountActionName: (name: string) => Promise<void>;
  handleArchiveAccountAction: () => Promise<void>;
  handleClickWorkflowValidationError: (index: number) => void;
}

const AccountActionHeader = ({
  isLoading,
  isDirty,
  isLatestPublishedVersion,
  isShowingWorkflowVersions,
  editable,
  accountTypeId,
  accountAction,
  actionWorkflow,
  actionWorkflowVersion,
  validationErrors,
  showWorkflowVersionsView,
  closeWorkflowVersionsView,
  handleArchiveAccountAction,
  handleUpdateAccountActionName,
  handleActivateAccountAction,
  handleDeactivateAccountAction,
  handleSaveAccountAction,
  handlePublishAccountAction,
  handleClickWorkflowValidationError,
}: AccountActionHeaderProps) => {
  const [
    showArchiveAccountActionModal,
    { prompt: archivePrompt, confirm: confirmArchive, cancel: cancelArchive },
  ] = useConfirmation();
  const [
    showActivateAccountActionModal,
    { prompt: activatePrompt, confirm: confirmActivate, cancel: cancelActivate },
  ] = useConfirmation();
  const [
    showDeactivateAccountActionModal,
    { prompt: deactivatePrompt, confirm: confirmDeactivate, cancel: cancelDeactivate },
  ] = useConfirmation();
  const [
    showPublishAccountActionModal,
    { prompt: publishPrompt, confirm: confirmPublish, cancel: cancelPublish },
  ] = useConfirmation();

  const handleClickArchiveAccountAction = () => archivePrompt(() => handleArchiveAccountAction());
  const handleClickActivateAccountAction = () =>
    activatePrompt(() => handleActivateAccountAction());
  const handleClickDeactivateAccountAction = () =>
    deactivatePrompt(() => handleDeactivateAccountAction());
  const handleClickPublishAccountAction = () => publishPrompt(() => handlePublishAccountAction());

  const [actionWorkflowName, setActionWorkflowName] = useState(actionWorkflow.attributes.name);

  const moreItems = [
    {
      id: 'account-action-header__dropdown-runs',
      label: 'Runs',
      routeTo: `/dashboard/account-types/${accountTypeId}/actions/${accountAction.id}/runs`,
    },
    {
      id: 'account-action-header__dropdown-versions',
      label: isShowingWorkflowVersions ? 'Close Version History' : 'Version History',
      onClick: isShowingWorkflowVersions ? closeWorkflowVersionsView : showWorkflowVersionsView,
    },
    {
      id: 'account-action-header__dropdown-archive',
      label: 'Archive',
      variant: 'danger' as const,
      onClick: handleClickArchiveAccountAction,
      hidden: !editable || actionWorkflow.attributes.status === WorkflowStatus.Archived,
    },
    {
      id: 'account-action-header__dropdown-activate',
      label: 'Activate',
      onClick: handleClickActivateAccountAction,
      hidden:
        !editable ||
        !actionWorkflow.relationships['latest-published-version']?.data ||
        actionWorkflow.attributes.status !== WorkflowStatus.Paused,
    },
    {
      id: 'account-action-header__dropdown-deactivate',
      label: 'Deactivate',
      onClick: handleClickDeactivateAccountAction,
      hidden:
        !editable ||
        !actionWorkflow.relationships['latest-published-version']?.data ||
        actionWorkflow.attributes.status !== WorkflowStatus.Running,
    },
  ].filter((item) => !item.hidden);

  return (
    <>
      <FullScreenHeaderContainer role='banner'>
        <HorizontalContainer>
          <NavBack
            to={`/dashboard/account-types/${accountTypeId}/settings/actions`}
            dataTest='account-actions-show__nav_back'
          />
          <DynamicInput
            disabled={!editable}
            value={actionWorkflowName}
            onBlur={() => handleUpdateAccountActionName(actionWorkflowName)}
            onChange={(e) => setActionWorkflowName(e.target.value)}
          />
          <StatusWrapper>
            <WorkflowStatusSticker workflowStatus={actionWorkflow.attributes.status} />
          </StatusWrapper>
          <StatusWrapper>
            <AccountActionVersionStatusSticker status={actionWorkflowVersion.attributes.status} />
          </StatusWrapper>
        </HorizontalContainer>
        <HStack spacing={Spacing[2]}>
          {validationErrors.length > 0 && (
            <WorkflowValidationErrorsDropdown
              validationErrors={validationErrors}
              onClickError={handleClickWorkflowValidationError}
            />
          )}
          <Button
            id='account-actions-show__save_button'
            aria-label='Save'
            onClick={handleSaveAccountAction}
            isPending={isLoading}
            disabled={isLoading || !editable || !isDirty}
          >
            Save
          </Button>
          <Button
            id='account-actions-show__publish_button'
            aria-label='Publish'
            onClick={handleClickPublishAccountAction}
            isPending={isLoading}
            disabled={isLoading || !editable || (!isDirty && isLatestPublishedVersion)}
          >
            Publish
          </Button>
          <DropdownMenu
            trigger={
              <Button
                aria-label='More actions'
                id='account-actions-show__button--dropdown'
                variant='inverse'
                width='icon'
                disabled={isLoading}
                prefixIcon={<IconMore />}
              />
            }
          >
            {moreItems.map((item) =>
              item.routeTo ? (
                <DropdownMenu.Item
                  as={Link}
                  id={item.id}
                  key={item.id}
                  disabled={isLoading}
                  to={item.routeTo}
                >
                  {item.label}
                </DropdownMenu.Item>
              ) : (
                <DropdownMenu.Item
                  id={item.id}
                  key={item.id}
                  variant={item.variant ?? 'basic'}
                  onClick={item.onClick}
                >
                  {item.label}
                </DropdownMenu.Item>
              ),
            )}
          </DropdownMenu>
        </HStack>
      </FullScreenHeaderContainer>
      <ModalWrapper isOpen={showArchiveAccountActionModal} handleCloseModal={cancelArchive}>
        <ConfirmationModal
          cancelAction={cancelArchive}
          confirmAction={confirmArchive}
          header='Archive this account action?'
          confirmActionText='Archive'
        >
          <p>
            This action is permanent and can’t be undone. Please ensure that this account action is
            not in use before continuing.
          </p>
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={showActivateAccountActionModal} handleCloseModal={cancelActivate}>
        <ConfirmationModal
          cancelAction={cancelActivate}
          confirmAction={confirmActivate}
          header='Activate this account action?'
          confirmActionText='Activate'
          confirmButtonType='primary'
        >
          <strong>{actionWorkflowName}</strong> will be activated across all environments.
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={showDeactivateAccountActionModal} handleCloseModal={cancelDeactivate}>
        <ConfirmationModal
          cancelAction={cancelDeactivate}
          confirmAction={confirmDeactivate}
          header='Deactivate this account action?'
          confirmActionText='Deactivate'
          confirmButtonType='primary'
        >
          <strong>{actionWorkflowName}</strong> will be deactivated across all environments.
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={showPublishAccountActionModal} handleCloseModal={cancelPublish}>
        <ConfirmationModal
          cancelAction={cancelPublish}
          confirmAction={confirmPublish}
          header='Publish this account action?'
          confirmActionText='Publish'
          confirmButtonType='primary'
        >
          <p>Publish an account action to activate it for your organization.</p>
        </ConfirmationModal>
      </ModalWrapper>
    </>
  );
};

export default AccountActionHeader;
